<template>
  <!--
    A PESCARIA (D3, D8): imersiva e sem moldura — a tela É o lugar. O cenário é o do mapa em que o
    barco está, não um gradiente genérico: mudou de mapa, mudou a água. Não há título; ninguém
    precisa ler "Aventura de Pesca" em cima da própria pesca.
  -->
  <FishScene :cena="cenaDoMapa" :escurecimento="0.2" class="pescaria">
    <!-- Onde estou, e a porta para a carta (7.5c): troca-se de ponto SEM sair da pescaria. -->
    <button type="button" class="pescaria__lugar" @click="cartaAberta = true">
      <FishIcon nome="map" tamanho="pequeno" />
      <span>{{ nomeDoMapa }}</span>
      <FishIcon nome="chevron-down" tamanho="miudo" />
    </button>

    <!-- Olhar de Águia (1.3): só chega para quem tem a habilidade — o servidor decide. -->
    <p v-if="cardumeLendario" class="pescaria__cardume t-legenda">
      <FishIcon nome="eye" tamanho="miudo" />
      <span>{{ $t('fishing.cardume', { nome: cardumeLendario }) }}</span>
    </p>

    <FishingAction />

    <FishModal
      :aberto="cartaAberta"
      :titulo="$t('maps.title')"
      largura="largo"
      @fechar="cartaAberta = false"
    >
      <CartaNautica folha @viajou="cartaAberta = false" />
    </FishModal>
  </FishScene>
</template>

<script>
import { mapState } from 'vuex';
import { FishScene, FishModal, FishIcon } from '@/shared/ui';
import FishingAction from '../shared/fishing/FishingAction.vue';
import CartaNautica from '@/shared/mapas/CartaNautica.vue';

/**
 * O cenário de cada mapa (D8) — os aquários do pack pago, com peixe nadando ("te dei background
 * de aquário", o autor, 03/09). Essas quatro cenas rendiam faixas chapadas por um bug de ORDEM DE
 * CAMADAS no recorte (o pack pago numera da frente para trás; a água ficava por cima do recife) —
 * consertado no `recortar-cenarios.mjs` em 03/09. A cena `praia` é um campo de minas navais
 * (recorte mal batizado) e não serve a mapa nenhum de pesca.
 *
 * Os dois últimos mapas não são debaixo d'água: as Ilhas Cazu ficam ACIMA das nuvens de densidade
 * e a Costa Dourada é uma luz dentro do Grande Nevoeiro. Vêm do pack de céu e nuvens
 * (`recortar-cenarios.mjs`, 03/09/2026).
 */
const CENA_DO_MAPA = {
  'Píer': 'recife-raso',
  'Pier': 'recife-raso',
  'Praia': 'recife-profundo',
  'Beach': 'recife-profundo',
  'Lago': 'agua-doce',
  'Lake': 'agua-doce',
  'Rio Amazonas': 'agua-doce',
  'Amazon River': 'agua-doce',
  'Mar Aberto': 'mar-aberto',
  'Open Sea': 'mar-aberto',
  'Ilhas Cazu': 'ilhas-cazu',
  'Cazu Islands': 'ilhas-cazu',
  'Costa Dourada': 'costa-dourada',
  'Golden Coast': 'costa-dourada',
};

export default {
  name: 'FishingPage',
  components: {
    FishScene,
    FishModal,
    FishIcon,
    FishingAction,
    CartaNautica
  },
  data() {
    return { cartaAberta: false };
  },
  computed: {
    ...mapState('player', ['currentMap', 'cardumeLendario']),
    cenaDoMapa() {
      return CENA_DO_MAPA[this.currentMap] || 'abissal';
    },
    nomeDoMapa() {
      const chaves = {
        'Píer': 'pier', 'Pier': 'pier',
        'Praia': 'beach', 'Beach': 'beach',
        'Lago': 'lake', 'Lake': 'lake',
        'Rio Amazonas': 'amazonRiver', 'Amazon River': 'amazonRiver',
        'Mar Aberto': 'openSea', 'Open Sea': 'openSea',
        'Ilhas Cazu': 'cazuIslands', 'Cazu Islands': 'cazuIslands',
        'Costa Dourada': 'goldenCoast', 'Golden Coast': 'goldenCoast',
      };
      const chave = chaves[this.currentMap];
      return chave ? this.$t(`maps.mapNames.${chave}`) : (this.currentMap || '');
    }
  }
};
</script>

<style scoped>
.pescaria {
  min-height: 100vh;
}

.pescaria__cardume {
  display: inline-flex;
  align-items: center;
  gap: var(--e-1);
  margin: var(--e-1) 0 0 var(--e-3);
  color: var(--ouro-claro);
  text-shadow: 1px 1px 0 var(--agua-abissal);
}

/* A placa do lugar: madeira pequena no canto, como os botões flutuantes — diz onde o barco está e
 * abre a carta. É a única moldura da tela, e é diegética (uma tabuleta). */
.pescaria__lugar {
  position: absolute;
  top: calc(var(--e-3) + env(safe-area-inset-top, 0px));
  left: var(--e-3);
  z-index: 5;

  display: flex;
  align-items: center;
  gap: var(--e-1);
  min-height: var(--alvo-minimo);
  padding: var(--e-1) var(--e-2);

  border-style: solid;
  border-color: transparent;
  border-width: var(--placa-borda);
  border-image-source: url('/ui/botao-neutro.png');
  border-image-slice: var(--placa-fatia);
  border-image-width: var(--placa-borda);
  border-image-repeat: repeat;
  background: var(--madeira);
  background-clip: padding-box;
  image-rendering: pixelated;

  font-family: var(--fonte-display);
  font-size: var(--f-2);
  color: var(--papel-luz);
  cursor: pointer;
}

@media (hover: hover) {
  .pescaria__lugar:hover {
    transform: translateY(-1px);
  }
}
</style>
